// ABOUTME: Hook for managing subscription data fetching and processing.
// ABOUTME: Uses Apollo Client to query products, subscriptions, and payments.

import { useMemo } from "react"
import { useQuery } from "@apollo/client/react"
import {
  ProductListForBuyDocument,
  SubscriptionListDocument,
  PaymentListForBuyerDocument,
  ProductType,
} from "@/graphql/main/gql/graphql"
import { useMeContext } from "@/model/user/provider"
import { isLiveSubscription, pickPrimarySubscriptions, pickScheduledSubscription } from "./subscriptionHelpers"
import type {
  PaymentListForBuyerQuery,
  ProductListForBuyQuery,
  SubscriptionListQuery,
  PaymentStatus,
} from "@/graphql/main/gql/graphql"
import type {
  ExtendedSubscriptionData,
  ExtendedSubscriptionItem,
} from "@/features/settings/components/Payment/types/subscription"

type _ProductItemType = NonNullable<ProductListForBuyQuery["productListForBuy"]>["items"][number]
type _SubscriptionItemType = NonNullable<SubscriptionListQuery["subscriptionList"]>["items"][number]
type PaymentItemType = NonNullable<PaymentListForBuyerQuery["paymentListForBuyer"]>["items"][number]
type _PaymentSubItemType = NonNullable<PaymentItemType>["items"][number]

// Helper to filter out undefined items from DeepPartialObject arrays
const filterDefined = <T,>(items: readonly (T | undefined)[] | undefined): T[] =>
  [...(items ?? [])].filter((item): item is T => item !== undefined)

export const useSubscriptions = () => {
  const { me } = useMeContext()
  // cache-and-network on subscription/product queries so the Payment tab
  // renders instantly from cache on revisit; network refresh happens silently.
  const { data: dataProductForBuy, loading: productLoading } = useQuery(ProductListForBuyDocument, {
    variables: { input: {} },
    fetchPolicy: "cache-and-network",
  })
  const {
    data: dataSubscriptionList,
    loading: subscriptionLoading,
    refetch: refetchSubscription,
  } = useQuery(SubscriptionListDocument, {
    variables: { filter: { statuses: [] } },
    fetchPolicy: "cache-and-network",
  })
  // paging: null = server default (newest 50). Enough here: payments are only
  // used to derive the payment status of the handful of current subscriptions.
  const { data: dataPaymentForBuy } = useQuery(PaymentListForBuyerDocument, {
    variables: { paging: null },
    fetchPolicy: "cache-and-network",
  })

  // Only surface "loading" to consumers when we have nothing to show. During
  // a background revalidation (cache-and-network) we keep previous data
  // rendered instead of flashing spinners over already-displayed cards.
  const isLoadProductList = productLoading && !dataProductForBuy
  const isLoadingSubscriptionList = subscriptionLoading && !dataSubscriptionList

  // Memoized so `products` (and the productVariants arrays derived from it)
  // keep a stable identity across unrelated re-renders — downstream effects
  // (e.g. PlanChangeModal selection reset) depend on that identity.
  const productItems = useMemo(() => filterDefined(dataProductForBuy?.productListForBuy.items), [dataProductForBuy])

  const products = useMemo(
    () => ({
      pro: productItems.find((item) => item.type === ProductType.SystemPro),
      proAll: productItems.filter((item) => item.type === ProductType.SystemPro),
      coach: productItems.find((item) => item.type === ProductType.SystemCoach),
      coachAll: productItems.filter((item) => item.type === ProductType.SystemCoach),
    }),
    [productItems],
  )

  const canceledSubscriptions: ExtendedSubscriptionData = useMemo(() => {
    const subscriptionItems = filterDefined(dataSubscriptionList?.subscriptionList.items)
    if (subscriptionItems.length === 0) {
      return { pro: [], coach: [], athletePro: [], scheduled: { pro: null, coach: null } }
    }

    // Group subscriptions by type. PRO has two flavours: rows the user pays
    // for themselves (userID === me.id) and rows a coach paid on the user's
    // behalf (userID !== me.id) — only the latter end up in athleteProSubscriptions.
    const proSubscriptions = subscriptionItems.filter(
      (item) => item.product?.type === ProductType.SystemPro && item.userID === me.id,
    )
    const coachSubscriptions = subscriptionItems.filter((item) => item.product?.type === ProductType.SystemCoach)
    // Only live rows: the coach's list is an operational view (toggle
    // auto-renewal, cancel), not a history — cancelled/expired rows would
    // pile up under it for every athlete.
    const athleteProSubscriptions = subscriptionItems.filter(
      (item) =>
        item.product?.type === ProductType.SystemPro && item.userID !== me.id && isLiveSubscription(item.status),
    )

    // Helper to transform subscription to ExtendedSubscriptionItem with proper types
    const toExtendedSubscriptionItem = (
      subscription: (typeof proSubscriptions)[number],
      paymentStatus?: PaymentStatus,
    ): ExtendedSubscriptionItem => ({
      id: subscription.id,
      createdAt: subscription.createdAt,
      updatedAt: subscription.updatedAt,
      currentPeriodStart: subscription.currentPeriodStart,
      currentPeriodEnd: subscription.currentPeriodEnd,
      status: subscription.status,
      isAutoRenewable: subscription.isAutoRenewable,
      isTrial: subscription.isTrial,
      appliedPromoCode: subscription.appliedPromoCode ?? null,
      discountAmount: subscription.discountAmount ?? null,
      nextPaymentAmount: subscription.nextPaymentAmount ?? null,
      paidByUserID: subscription.paidByUserID ?? null,
      userID: subscription.userID,
      canceledAt: subscription.canceledAt ?? null,
      endedAt: subscription.endedAt ?? null,
      source: subscription.source,
      isUpgradeAwaitingPayment: subscription.isUpgradeAwaitingPayment,
      product: subscription.product ? { id: subscription.product.id, type: subscription.product.type } : null,
      paymentStatus,
    })

    // Функция для добавления статуса paid к подпискам
    const addPaymentStatus = (subscriptions: typeof proSubscriptions): ExtendedSubscriptionItem[] => {
      const paymentItems = filterDefined(dataPaymentForBuy?.paymentListForBuyer.items)
      if (paymentItems.length === 0) {
        return subscriptions.map((subscription) => toExtendedSubscriptionItem(subscription))
      }

      return subscriptions.map((subscription) => {
        // Ищем соответствующий платеж по типу продукта и датам
        const relatedPayment = paymentItems.find((payment) => {
          // Проверяем тип продукта в платеже
          const paymentSubItems = filterDefined(payment.items)
          const paymentProductType = paymentSubItems.find((item) => item.product?.type === subscription.product?.type)
            ?.product?.type

          if (paymentProductType !== subscription.product?.type) {
            return false
          }

          // Проверяем даты - paidAt должен быть близок к currentPeriodStart
          if (payment.paidAt) {
            const paymentDate = new Date(payment.paidAt)
            const subscriptionDate = new Date(subscription.currentPeriodStart)
            const timeDiff = Math.abs(paymentDate.getTime() - subscriptionDate.getTime())
            const daysDiff = timeDiff / (1000 * 60 * 60 * 24)

            // Платеж должен быть в пределах 7 дней от начала периода подписки
            const isDateMatch = daysDiff <= 7

            return isDateMatch
          }

          return false
        })

        return toExtendedSubscriptionItem(subscription, relatedPayment?.status ?? undefined)
      })
    }

    const primaryProSubscriptions = pickPrimarySubscriptions(proSubscriptions)
    const primaryCoachSubscriptions = pickPrimarySubscriptions(coachSubscriptions)
    const scheduledPro = pickScheduledSubscription(proSubscriptions)
    const scheduledCoach = pickScheduledSubscription(coachSubscriptions)

    return {
      pro: addPaymentStatus(primaryProSubscriptions),
      coach: addPaymentStatus(primaryCoachSubscriptions),
      athletePro: addPaymentStatus(athleteProSubscriptions),
      scheduled: {
        pro: scheduledPro ? toExtendedSubscriptionItem(scheduledPro) : null,
        coach: scheduledCoach ? toExtendedSubscriptionItem(scheduledCoach) : null,
      },
    }
  }, [dataSubscriptionList, dataPaymentForBuy, me.id])

  return {
    products,
    canceledSubscriptions,
    isLoadProductList,
    isLoadingSubscriptionList,
    refetchSubscription,
  }
}
